import React, { useCallback, useRef } from 'react';
import EventUtility from '../../../../utils/EventUtility';

type ProgressBarProps = {
	position: number;
	setPosition: (position: number) => void;
	duration: number;
	onDragStart: (position: number) => void;
	onDragEnd: (event: any) => void;
	onDraggedPositionChange: (position: number) => void;
	isDragging: boolean;
};

export const ProgressBar: React.FC<ProgressBarProps> = ({ position, setPosition, duration, onDragStart: onDragStartProp, onDragEnd, onDraggedPositionChange, isDragging }) => {
	const progressBar = useRef(null);
	const calculatePositionFromEvent = useCallback(
		(event) => {
			const offset = new EventUtility(event).getClientX();
			const element = progressBar.current;
			if (element) {
				const offsetLeft = element.getBoundingClientRect().x;
				const percentage = (offset - offsetLeft) / element.clientWidth;
				return Math.floor(duration * percentage);
			}
			return 0;
		},
		[duration]
	);

	const onDragStart = useCallback(
		(event) => {
			event.stopPropagation();
			onDragStartProp(calculatePositionFromEvent(event));
		},
		[calculatePositionFromEvent, onDragStartProp]
	);

	const onDrag = useCallback(
		(event) => {
			if (isDragging) {
				onDraggedPositionChange(calculatePositionFromEvent(event));
			}
		},
		[calculatePositionFromEvent, isDragging, onDraggedPositionChange]
	);

	const onPositionSelected = useCallback(
		(event) => {
			event.preventDefault();

			if (isDragging) return;

			const element = progressBar.current;

			if (element) {
				const offsetLeft = element.getBoundingClientRect().x;
				const percentage = (event.clientX - offsetLeft) / element.clientWidth;
				setPosition(Math.floor(duration * percentage));
			}
		},
		[duration, isDragging, setPosition]
	);

	let percentage = 0;
	if (duration > 0) {
		percentage = (position / duration) * 100;
	}

	return (
		<div
			role="presentation"
			className={'progress-bar-container'}
			onMouseDown={onDragStart}
			onMouseLeave={onDragEnd}
			onMouseUp={onDragEnd}
			onMouseMove={onDrag}
			onTouchStart={onDragStart}
			onTouchEnd={onDragEnd}
			onTouchMove={onDrag}
			onClick={onPositionSelected}
		>
			<div ref={progressBar} className={'progress-bar'}>
				<div className={'progress-elapsed'} style={{ width: `${percentage}%` }} />
				<div className={'progress-indicator'} />
				<div className={'progress-remaining'} style={{ width: `${100 - percentage}%` }} />
			</div>
		</div>
	);
};
